import proj4 from 'proj4';
import type { CrsDefinition } from './crs';

export interface BasemapProvider {
  id: string;
  label: string;
  url: string;
  attribution: string;
  maxZoom: number;
  tileSize: number;
  // Google: the viewport endpoint whose answer is the attribution for the current view.
  viewport?: string | null;
}
export interface BasemapConfig { providers: BasemapProvider[]; terrainUrl: string | null }

// Where the map's position comes from: the file's grid, the project pin (or
// the placement fine-tuned from it), or 'auto' = the grid when it fits the scan.
export type BasemapPosition = 'auto' | 'file' | 'pin';
// A fine-tune on top of the grid: the map turned `rotation` degrees
// counter-clockwise about the pivot, then slid by (dx, dy), in scene metres.
export interface GridAdjust { dx: number; dy: number; rotation: number; pivotX: number; pivotY: number }
export interface ProjectBasemap {
  provider: string; opacity: number; terrain: boolean; height: number | null;
  position: BasemapPosition; gridAdjust: GridAdjust | null;
}
export interface SitePlacement { lat: number; lng: number; heading: number; originX: number; originY: number; originZ: number }

export const DEFAULT_PROJECT_BASEMAP: ProjectBasemap = { provider: 'off', opacity: 1, terrain: true, height: null, position: 'auto', gridAdjust: null };

const finite = (...v: unknown[]): boolean => v.every((x) => typeof x === 'number' && Number.isFinite(x));
const gridAdjustOf = (a: Partial<GridAdjust> | null | undefined): GridAdjust | null =>
  a && finite(a.dx, a.dy, a.rotation, a.pivotX, a.pivotY) && (a.dx || a.dy || a.rotation)
    ? { dx: a.dx!, dy: a.dy!, rotation: a.rotation!, pivotX: a.pivotX!, pivotY: a.pivotY! }
    : null;

// Only the saved fields: what comes back from a query carries __typename,
// which the save mutation's input would reject.
export const projectBasemapOf = (saved: Partial<ProjectBasemap> | null | undefined): ProjectBasemap => ({
  provider: typeof saved?.provider === 'string' && saved.provider ? saved.provider : DEFAULT_PROJECT_BASEMAP.provider,
  opacity: typeof saved?.opacity === 'number' && Number.isFinite(saved.opacity) ? saved.opacity : DEFAULT_PROJECT_BASEMAP.opacity,
  terrain: typeof saved?.terrain === 'boolean' ? saved.terrain : DEFAULT_PROJECT_BASEMAP.terrain,
  height: typeof saved?.height === 'number' && Number.isFinite(saved.height) ? saved.height : null,
  position: saved?.position === 'file' || saved?.position === 'pin' ? saved.position : 'auto',
  gridAdjust: gridAdjustOf(saved?.gridAdjust),
});

// Base map changes in the Scene apply to that view only until saved; this
// decides whether the rail offers Save / Revert.
export const sameProjectBasemap = (
  a: Partial<ProjectBasemap> | null | undefined,
  b: Partial<ProjectBasemap> | null | undefined,
): boolean => {
  const x = projectBasemapOf(a);
  const y = projectBasemapOf(b);
  return x.provider === y.provider && x.terrain === y.terrain && x.height === y.height
    && Math.abs(x.opacity - y.opacity) < 1e-9 && x.position === y.position
    && JSON.stringify(x.gridAdjust) === JSON.stringify(y.gridAdjust);
};

export const sitePlacementOf = (p: Partial<SitePlacement> | null | undefined): SitePlacement | null =>
  p && finite(p.lat, p.lng, p.heading, p.originX, p.originY, p.originZ)
    ? { lat: p.lat!, lng: p.lng!, heading: p.heading!, originX: p.originX!, originY: p.originY!, originZ: p.originZ! }
    : null;
export const sameSitePlacement = (a: Partial<SitePlacement> | null | undefined, b: Partial<SitePlacement> | null | undefined): boolean =>
  JSON.stringify(sitePlacementOf(a)) === JSON.stringify(sitePlacementOf(b));

// Whether the Scene offers a base map at all: something has to say where the
// site is. A grid or a placement draws the map; a project location alone is
// where "Place on map…" starts.
export const basemapLocatable = (
  location: { latitude?: number | null; longitude?: number | null } | null | undefined,
  crs: CrsDefinition | null,
  placement: SitePlacement | null | undefined,
): boolean => !!crs || !!placement || (location?.latitude != null && location?.longitude != null);

// How scene coordinates become map coordinates: the project's grid when it
// has one, else where the user placed the site on the map.
export type BasemapFrame = { crs: string; adjust?: GridAdjust | null } | { placement: SitePlacement } | null;
export const basemapFrame = (crs: CrsDefinition | null, placement: SitePlacement | null | undefined): BasemapFrame =>
  crs ? { crs: crs.proj4 } : placement ? { placement } : null;

type Location = { latitude?: number | null; longitude?: number | null } | null | undefined;
type Point = { x: number; y: number; z?: number };

const GEOREF_MIN_M = 10_000;
const PIN_TOLERANCE_M = 20_000;

const distanceM = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const r = Math.PI / 180;
  const a = Math.sin(((lat2 - lat1) * r) / 2) ** 2
    + Math.cos(lat1 * r) * Math.cos(lat2 * r) * Math.sin(((lng2 - lng1) * r) / 2) ** 2;
  return 2 * 6371000 * Math.asin(Math.min(1, Math.sqrt(a)));
};

// Whether the scene's grid can place the map. A scanner's own local
// coordinates (near 0,0) read as British National Grid are open sea off the
// Isles of Scilly, and a wrong code or zone puts the site hundreds of km from
// the project's pin; either way the grid would draw the wrong place.
export type GridFit = 'fits' | 'local' | 'far';
export const gridFit = (crs: CrsDefinition | null, centre: Point | null, location: Location): GridFit => {
  if (!crs || !centre) return 'fits';
  if (Math.abs(centre.x) < GEOREF_MIN_M && Math.abs(centre.y) < GEOREF_MIN_M) return 'local';
  if (location?.latitude == null || location?.longitude == null) return 'fits';
  let lonLat: number[];
  try {
    lonLat = proj4(crs.proj4).inverse([centre.x, centre.y]);
  } catch {
    return 'far';
  }
  if (!lonLat.every(Number.isFinite)) return 'far';
  return distanceM(lonLat[1], lonLat[0], location.latitude, location.longitude) > PIN_TOLERANCE_M ? 'far' : 'fits';
};

// The project pin under the centre of the scan, north up: where a placement
// starts before the user fine-tunes it.
export const pinPlacement = (location: Location, centre: Point | null): SitePlacement | null =>
  centre && location?.latitude != null && location?.longitude != null
    ? { lat: location.latitude, lng: location.longitude, heading: 0, originX: centre.x, originY: centre.y, originZ: centre.z ?? 0 }
    : null;

// The frame the Scene draws the map in, and where it came from. 'file' and
// 'pin' are the user's pick; 'auto' takes the grid when it fits the scan,
// else the placement, else the pin. A pick that is not available falls back.
export type BasemapFrameSource = 'crs' | 'placement' | 'pin';
export const sceneBasemapFrame = (o: {
  crs: CrsDefinition | null;
  fit: GridFit;
  position: BasemapPosition;
  gridAdjust: GridAdjust | null;
  placement: SitePlacement | null | undefined;
  location: Location;
  centre: Point | null;
}): { frame: BasemapFrame; source: BasemapFrameSource | null } => {
  const grid = o.crs ? { frame: { crs: o.crs.proj4, adjust: o.gridAdjust }, source: 'crs' as const } : null;
  const pin = o.placement
    ? { frame: { placement: o.placement }, source: 'placement' as const }
    : (() => { const p = pinPlacement(o.location, o.centre); return p ? { frame: { placement: p }, source: 'pin' as const } : null; })();
  const order = o.position === 'file' ? [grid, pin]
    : o.position === 'pin' ? [pin, grid]
      : [o.fit === 'fits' ? grid : null, pin];
  return order.find(Boolean) ?? { frame: null, source: null };
};

// Fine-tune steps are given as the user sees the screen: forward = away from
// the camera, right = to its right, for a camera looking along `heading`
// (radians from scene +X, counter-clockwise).
export const screenShift = (heading: number, forward: number, right: number): { x: number; y: number } => ({
  x: forward * Math.cos(heading) + right * Math.sin(heading),
  y: forward * Math.sin(heading) - right * Math.cos(heading),
});

const turnOf = (deg: number): number => ((((deg + 180) % 360) + 360) % 360) - 180;

// Slide the map by `shift` and turn it `turn` degrees counter-clockwise, on
// top of the grid; the first nudge pins the turn's pivot to the scan centre.
export const nudgeGridAdjust = (a: GridAdjust | null, pivot: Point, shift: { x: number; y: number }, turn: number): GridAdjust => {
  const base = a ?? { dx: 0, dy: 0, rotation: 0, pivotX: pivot.x, pivotY: pivot.y };
  return { ...base, dx: base.dx + shift.x, dy: base.dy + shift.y, rotation: turnOf(base.rotation + turn) };
};

// The same nudge for a placement: the scene point at the pin moves with the
// map, and the map's north turns with it.
export const nudgePlacement = (p: SitePlacement, shift: { x: number; y: number }, turn: number): SitePlacement => ({
  ...p,
  originX: p.originX + shift.x,
  originY: p.originY + shift.y,
  heading: (((p.heading + turn) % 360) + 360) % 360,
});

export interface BasemapEngineConfig {
  frame: BasemapFrame;
  provider: { id: string; url: string; attribution: string; maxZoom: number; tileSize: number; viewport: string | null };
  opacity: number;
  terrainUrl: string | null;
  height: number | null;
}

// The message the Scene posts to the engine; null switches the map off.
export const basemapEngineConfig = (
  prefs: ProjectBasemap,
  config: BasemapConfig | null | undefined,
  frame: BasemapFrame,
): BasemapEngineConfig | null => {
  if (!config || !frame || prefs.provider === 'off') return null;
  const provider = config.providers.find((p) => p.id === prefs.provider);
  if (!provider) return null;
  return {
    frame,
    provider: {
      id: provider.id, url: provider.url, attribution: provider.attribution, maxZoom: provider.maxZoom,
      tileSize: provider.tileSize || 256, viewport: provider.viewport ?? null,
    },
    opacity: prefs.opacity,
    terrainUrl: prefs.terrain ? config.terrainUrl : null,
    height: prefs.height,
  };
};

export type BasemapState = 'off' | 'on' | 'noFrame' | 'noProvider';

// Why the map is or is not drawn, for the rail.
export const basemapState = (prefs: ProjectBasemap, config: BasemapConfig | null | undefined, frame: BasemapFrame): BasemapState => {
  if (prefs.provider === 'off') return 'off';
  if (!config || !config.providers.some((p) => p.id === prefs.provider)) return 'noProvider';
  return frame ? 'on' : 'noFrame';
};

// A saved provider that is no longer offered (key removed) falls back to the
// first one, so the map still shows.
export const providerOrFallback = (saved: string, config: BasemapConfig | null | undefined): string => {
  if (saved === 'off' || !config) return saved;
  return config.providers.some((p) => p.id === saved) ? saved : (config.providers[0]?.id ?? 'off');
};
